/**
 * Unguessable identifiers, for values where guessing one grants access.
 *
 * Found 2026-09-05 by activating the `no-restricted-syntax` lint rules, which
 * had been sitting in `package.json`'s `eslintConfig` where ESLint never read
 * them — `.eslintrc.json` takes precedence, so they had never fired.
 *
 * The code they caught was mostly GOOD: join codes and session ids already
 * used `crypto.getRandomValues` / `crypto.randomUUID` on the primary path. The
 * problem was the fallback. Every one of them ended in
 *
 *     return Math.random().toString(36).substring(2, 8).toUpperCase();
 *
 * so if `crypto` were ever unavailable the code would silently downgrade to a
 * predictable value — and a predictable collaboration join code lets a stranger
 * into a child's session. A security control that quietly degrades is worse
 * than one that breaks loudly, because nobody finds out.
 *
 * These helpers therefore FAIL CLOSED. `crypto.getRandomValues` has been in
 * every browser since IE11 and in every secure context, so the throw is
 * unreachable in practice; it exists so the degraded path cannot exist.
 *
 * Use these for join codes, share links, invite tokens — anything where
 * guessing the value is the attack. For correlation ids (log keys, React list
 * keys, telemetry session ids) `Math.random` is fine; annotate those call sites
 * with an eslint-disable and a reason rather than reaching for this.
 */

/** Ambiguity-free alphabet: no O/0, I/1, so codes survive being read aloud. */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const getCrypto = (): Crypto => {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (!c || typeof c.getRandomValues !== 'function') {
    throw new Error(
      'Secure random values are unavailable in this environment. Refusing to ' +
        'generate a guessable code — see src/utils/secureId.ts.'
    );
  }
  return c;
};

/**
 * A short human-typable code, e.g. a collaboration or family-session join code.
 *
 * Uses rejection sampling rather than `% alphabet.length`, which would bias
 * toward the first 8 characters (256 is not a multiple of 32... it is here, but
 * the alphabet is a constant someone will edit one day, and a biased join code
 * is a smaller keyspace).
 *
 * Default length 6 over a 32-character alphabet is ~30 bits.
 */
export const secureCode = (length = 6, alphabet: string = CODE_ALPHABET): string => {
  const c = getCrypto();
  const max = Math.floor(256 / alphabet.length) * alphabet.length;
  let out = '';

  while (out.length < length) {
    const buf = new Uint8Array(length - out.length);
    c.getRandomValues(buf);
    for (let i = 0; i < buf.length && out.length < length; i++) {
      if (buf[i] < max) out += alphabet.charAt(buf[i] % alphabet.length);
    }
  }
  return out;
};

/**
 * A long unguessable identifier, optionally prefixed for readability in logs.
 * Backed by `crypto.randomUUID` where available, otherwise assembled from
 * `getRandomValues` — never from `Math.random`.
 */
export const secureId = (prefix?: string): string => {
  const c = getCrypto();
  const raw =
    typeof c.randomUUID === 'function'
      ? c.randomUUID().replace(/-/g, '')
      : Array.from(c.getRandomValues(new Uint8Array(16)))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
  return prefix ? `${prefix}_${raw}` : raw;
};

/**
 * A hex token for links mailed to a parent or teacher.
 * @param bytes entropy in bytes; 32 bytes = 256 bits, matching the server.
 */
export const secureToken = (bytes = 32): string => {
  const c = getCrypto();
  return Array.from(c.getRandomValues(new Uint8Array(bytes)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};
